package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.Strings
import com.ilussobsa.sdk.currentUser
import com.ilussobsa.sdk.currentUserSession
import com.ilussobsa.utils.*
import com.lightningkite.kiteui.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.lightningdb.*
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlinx.coroutines.*


@Routable("/settings")
class SettingsScreen() : Page {
    val userInProgress = Signal<User>(User(email = ""))
    val startUser = Signal<User>(User(email = ""))
    override val title: Reactive<String> = remember {
        listOfNotNull(
            Strings.settings,
        ).joinToString(" ")
    }

    override fun ViewWriter.render() = scrolls - col {
        launch {
            startUser set currentUserSession().me.awaitNotNull()
            userInProgress set currentUserSession().me.awaitNotNull()
        }
        card - col {
            h3(Strings.accountInformation)
            padded - col {

                centered - button {
                    profilePicture(userInProgress)
                    onClick {
                        ExternalServices.requestFile(listOf("image/png", "image/jpeg")) { image ->
                            if (image == null) return@requestFile
                            launch {
                                val session = currentUserSession()
                                try {
                                    val futureCallToken =
                                        session.api.uploadFileForRequest().upload(RequestBodyFile(image))
                                    tempImagePointers[futureCallToken] = ImageLocal(image)
                                    userInProgress.modify { it.copy(profilePicture = futureCallToken) }
                                } catch (e: Exception) {
                                    Exception("Error updating profile picture", e).report()
                                    alert(Strings.error, Strings.failedToUploadPicture)
                                }
                            }
                        }
                    }
                }

                centered - row {
                    // TODO: sizeConstraints(width = 8.rem) not working on iOS
                    centered - sizeConstraints(width = 8.rem) - text(Strings.email1)
                    sizeConstraints(width = 15.rem) - fieldTheme - disabled - textField {
                        enabled = false
                        content bind userInProgress.prop(User.path.email)
                    }
                }
                centered - row {
                    centered - sizeConstraints(width = 8.rem) - text(Strings.phoneNumber)
                    sizeConstraints(width = 15.rem) - fieldTheme - textField {
                        content bind userInProgress.prop(User.path.phoneNumber).notNull("")
                    }
                }
                centered - row {
                    centered - sizeConstraints(width = 8.rem) - text(Strings.name2)
                    sizeConstraints(width = 15.rem) - fieldTheme - textField {

                        content bind userInProgress.prop(User.path.name)
                    }
                }


                gravity(Align.End, Align.Center) - compact - important - button {
                    text {
                        ::content {
                            if (userInProgress() != startUser()) Strings.save else Strings.saved
                        }
                    }
                    ::enabled {
                        userInProgress() != startUser()
                    }
                    onClick {
                        launch {
                            val modification = modification<User> {
                                it.email assign userInProgress.await().email
                                it.phoneNumber assign userInProgress.await().phoneNumber
                                it.name assign userInProgress.await().name
                                it.profilePicture assign userInProgress.await().profilePicture
                            }
                            try {
                                currentUserSession().user[currentUserSession().userId].modify(modification)
                                    ?.let {
                                        startUser set it
                                        userInProgress set it
                                    }
                            } catch (e: Exception) {
                                alert(Strings.error, Strings.updateFailed1)
                            }
                        }
                    }
                }
            }
        }
        card - col {
            shown = false
            ::shown { currentUser()?.let { it.role >= UserRole.Manager } == true && listOf(ApiOption.Dev, ApiOption.Staging, ApiOption.Local).contains(selectedApi()) }
            menuButton {
                text("Admin actions")
                opensMenu {
                    col {
                        button {
                            text("Reset data")
                            onClick {
                                closePopovers()
                                alert(
                                    Strings.resetResult,
                                    currentUserSession().uncached.faker.resetTestData(ResetDataRequest(false))
                                )
                            }
                        }
                        button {
                            text("Reset data with proxies")
                            onClick {
                                closePopovers()
                                alert(
                                    Strings.resetResult,
                                    currentUserSession().uncached.faker.resetTestData(ResetDataRequest(proxyBurstMode = true))
                                )
                            }
                        }
                        button {
                            text("Reset data just finished")
                            onClick {
                                closePopovers()
                                alert(
                                    Strings.resetResult,
                                    currentUserSession().uncached.faker.resetTestData(ResetDataRequest(postAuction = true))
                                )
                            }
                        }
                        button {
                            text("Clear test data")
                            onClick {
                                closePopovers()
                                alert(
                                    Strings.resetResult,
                                    currentUserSession().uncached.faker.resetTestData(ResetDataRequest(justClean = true))
                                )
                            }
                        }
                        button {
                            text("Save auction state")
                            onClick {
                                closePopovers()
                                alert(Strings.resetResult, currentUserSession().uncached.faker.saveState())
                            }
                        }
                        button {
                            text("Load auction state")
                            onClick {
                                closePopovers()
                                alert(Strings.resetResult, currentUserSession().uncached.faker.loadState())
                            }
                        }
                        button {
                            text("Send error report in background")
                            onClick {
                                closePopovers()
                                Exception("Test error").report()
                            }
                        }
                        button {
                            text("Test Audio")
                            var index = 0
                            onClick {
                                closePopovers()
                                Auctioneer.Recording1.audioPack().next.audio.let { it[index++ % it.size] }.load().play()
                                repeat(10) {

                                    gc()
                                }
                            }
                        }
                        button {
                            text("GC")
                            var index = 0
                            onClick {
                                closePopovers()
                                repeat(10) {
                                    cleanImageCache()
                                    println(gc().usage.div(1_000_000.0).toString() + " mb")
                                }
                            }
                        }
                    }
                }
            }
        }
        card - col {
            h3(Strings.support)
            rowCollapsingToColumn(50.rem) {
                expanding - card - externalLink {
                    centered - text("Email support@lussotechnologies.com")
                    to = "mailto:support@lussotechnologies.com"
                }
//                    expanding - card - externalLink {
//                        centered - text("Call 000-000-0000")
//                        to = "tel:0000000000"
//                    }
            }
        }
    }
}

